import { motion } from 'framer-motion';
import { useComputedColorScheme } from '@mantine/core';

export default function ProjectLoader({
  size = 75,
  colorOverride,
  className = '',
  cycleDuration = 0.5,
  strokeWidth = 2.5,
  innerRadius = 11.5,
  moonRadius = 7,
  delay = 1,
  invisibleColor = 'var(--mantine-color-body)',
}: {
  size?: number;
  strokeWidth?: number;
  colorOverride?: string;
  className?: string;
  cycleDuration?: number;
  innerRadius?: number;
  moonRadius?: number;
  delay?: number;
  invisibleColor?: string;
}) {
  const colorScheme = useComputedColorScheme();
  const isDark = colorScheme === 'dark';

  const color = colorOverride || (isDark ? 'var(--mantine-color-dimmed)' : 'var(--mantine-color-gray-3)');

  // Set sizes and positions
  const viewBoxSize = size / 1.25;
  const center = viewBoxSize / 2;
  const calculatedStrokeWidth = strokeWidth ?? size / 12.5;
  const radius = center - calculatedStrokeWidth * 2;
  const calculatedInnerRadius = innerRadius ?? radius * 0.6;

  const outerCircumference = 2 * Math.PI * radius;

  // Animation variants
  const outerCircleVariants = {
    initial: {
      strokeDasharray: `${outerCircumference}`,
      strokeDashoffset: `${outerCircumference}`,
      rotate: 180,
      opacity: 1,
    },
    animate: {
      strokeDashoffset: `${outerCircumference / 2}`,
      rotate: [225, -135],
      opacity: 0,
      transition: {
        rotate: {
          duration: cycleDuration, // Full cycle for rotation
          ease: 'linear',
          repeat: 3,
          repeatType: 'loop',
        },
        opacity: {
          delay,
          duration: 0,
        },
      },
    },
  };

  const intermediateCircleVariants = {
    initial: {
      strokeDasharray: outerCircumference,
      strokeDashoffset: outerCircumference,
      opacity: 0,
      rotate: 180,
    },
    animate: {
      strokeDashoffset: 0,
      opacity: 1,
      rotate: [225, -135],
      transition: {
        delay,
        duration: 1,
        opacity: { delay, duration: 0 },
      },
    },
  };

  const oppositeIntermediateCircleVariants = {
    initial: {
      strokeDasharray: outerCircumference,
      strokeDashoffset: outerCircumference / 2, // Start with semicircle
      opacity: 0,
      rotate: 225,
    },
    animate: {
      strokeDashoffset: outerCircumference, // Undraw the circle
      opacity: 1,
      transition: {
        delay,
        duration: 0.5,
        opacity: { delay, duration: 0 },
      },
    },
  };

  const smallCircleVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        delay,
        duration: 0.2,
      },
    },
  };

  return (
    <div
      className={`flex flex-col items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        initial="initial"
        animate="animate"
      >
        {/* Outer animated circle */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={calculatedStrokeWidth}
          strokeLinecap="round"
          variants={outerCircleVariants}
          className="origin-center"
        />

        {/* Intermediate circle */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={calculatedStrokeWidth}
          strokeLinecap="round"
          style={{ transformOrigin: `${center}px ${center}px` }}
          variants={intermediateCircleVariants}
          initial="initial"
          animate="animate"
        />

        {/* Opposite direction intermediate circle */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={calculatedStrokeWidth}
          strokeLinecap="round"
          style={{ transformOrigin: `${center}px ${center}px` }}
          variants={oppositeIntermediateCircleVariants}
          initial="initial"
          animate="animate"
        />

        {/* Inner static circle */}
        <circle
          cx={center}
          cy={center}
          r={calculatedInnerRadius}
          fill={color}
          stroke={color}
          strokeWidth={calculatedStrokeWidth}
          strokeLinecap="round"
        />

        {/* Small circle at top left with background to hide outer circle */}
        <motion.circle
          cx={center - radius * 0.7071} // ~1/sqrt(2), center of the intersection of outer animated circle and this
          cy={center - radius * 0.7071}
          r={moonRadius}
          fill={invisibleColor}
          variants={smallCircleVariants}
        />
        <motion.circle
          cx={center - radius * 0.7071}
          cy={center - radius * 0.7071}
          r={moonRadius * 0.7071}
          fill={color}
          variants={smallCircleVariants}
        />
      </motion.svg>
    </div>
  );
}
